import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, Search, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface FAQItem {
    question: string;
    answer: string;
    category: string;
}

const FAQPage: React.FC = () => {
    const { t } = useTranslation();
    const [openIndex, setOpenIndex] = useState<number | null>(null);
    const [searchTerm, setSearchTerm] = useState('');

    // Map question keys to their categories
    const faqStructure = [
        { key: 'how_to_start', category: 'initial_setup' },
        { key: 'technical_knowledge', category: 'initial_setup' },
        { key: 'connect_whatsapp', category: 'whatsapp_serpapi' },
        { key: 'what_is_serpapi', category: 'whatsapp_serpapi' },
        { key: 'whatsapp_disconnect', category: 'whatsapp_serpapi' },
        { key: 'agent_difference', category: 'ai_agents' },
        { key: 'configure_dispatch_agent', category: 'ai_agents' },
        { key: 'attendance_agent_availability', category: 'ai_agents' },
        { key: 'efficient_search', category: 'search_filters' },
        { key: 'available_filters', category: 'search_filters' },
        { key: 'whatsapp_verification', category: 'search_filters' },
        { key: 'plan_differences', category: 'plans_features' },
        { key: 'how_to_upgrade', category: 'plans_features' },
        { key: 'can_cancel', category: 'plans_features' },
    ];

    const faqData: FAQItem[] = faqStructure.map(item => ({
        question: t(`faq.questions.${item.key}.question`),
        answer: t(`faq.questions.${item.key}.answer`),
        category: t(`faq.categories.${item.category}`)
    }));

    const categories = Array.from(new Set(faqData.map(item => item.category)));

    const filteredFAQ = faqData.filter(item =>
        item.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.answer.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="p-6 lg:p-10 max-w-4xl mx-auto flex flex-col gap-8"
        >
            <div>
                <Link to="/documentacao" className="inline-flex items-center gap-2 text-primary hover:underline mb-4">
                    <ArrowLeft className="size-4" />
                    {t('faq.back_to_documentation')}
                </Link>

                <motion.h1
                    className="text-4xl font-black leading-tight tracking-[-0.033em] text-transparent bg-clip-text bg-gradient-primary mb-2"
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1, duration: 0.6 }}
                >
                    {t('faq.page_title')}
                </motion.h1>
                <p className="text-lg text-gray-600 dark:text-gray-400">
                    {t('faq.page_subtitle')}
                </p>
            </div>

            {/* Search Bar */}
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="relative"
            >
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 size-5 text-gray-400" />
                <input
                    type="text"
                    placeholder={t('faq.search_placeholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full h-12 pl-12 pr-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-[#181411] dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary"
                />
            </motion.div>

            {/* FAQ Items by Category */}
            <div className="flex flex-col gap-8">
                {categories.map((category, catIndex) => {
                    const categoryItems = filteredFAQ.filter(item => item.category === category);

                    if (categoryItems.length === 0) return null;

                    return (
                        <motion.div
                            key={category}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3 + catIndex * 0.1 }}
                        >
                            <h2 className="text-2xl font-bold text-[#181411] dark:text-white mb-4">
                                {category}
                            </h2>

                            <div className="flex flex-col gap-3">
                                {categoryItems.map((item, index) => {
                                    const globalIndex = faqData.indexOf(item);
                                    const isOpen = openIndex === globalIndex;

                                    return (
                                        <motion.div
                                            key={globalIndex}
                                            className="card overflow-hidden"
                                            initial={{ opacity: 0, x: -20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: 0.4 + index * 0.05 }}
                                        >
                                            <button
                                                onClick={() => setOpenIndex(isOpen ? null : globalIndex)}
                                                className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                                            >
                                                <span className="font-semibold text-[#181411] dark:text-white pr-4">
                                                    {item.question}
                                                </span>
                                                <motion.div
                                                    animate={{ rotate: isOpen ? 180 : 0 }}
                                                    transition={{ duration: 0.2 }}
                                                >
                                                    <ChevronDown className="size-5 text-gray-500 shrink-0" />
                                                </motion.div>
                                            </button>

                                            {isOpen && (
                                                <motion.div
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: 'auto', opacity: 1 }}
                                                    transition={{ duration: 0.2 }}
                                                >
                                                    <div className="px-4 pb-4 text-gray-600 dark:text-gray-400 border-t border-gray-100 dark:border-gray-700/50 pt-4">
                                                        {item.answer}
                                                    </div>
                                                </motion.div>
                                            )}
                                        </motion.div>
                                    );
                                })}
                            </div>
                        </motion.div>
                    );
                })}
            </div>

            {filteredFAQ.length === 0 && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="card p-8 text-center"
                >
                    <p className="text-gray-500 dark:text-gray-400">
                        {t('faq.no_results', { searchTerm })}
                    </p>
                </motion.div>
            )}
        </motion.div>
    );
};

export default FAQPage;
